import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  TablePagination,
  Box,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const CrewingAgentFormIList = () => {
  const { crewingAgentId } = useParams();
  const [formIData, setFormIData] = useState([]);
  const [formDetails, setFormDetails] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  useEffect(() => {
    const fetchFormIData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        const params = { crewingAgentId };
        if (selectedMonth) params.month = selectedMonth;
        if (selectedYear) params.year = selectedYear;

        const response = await axios.get(`${baseURL}/api/getForm1DateWiseByAgent`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Fetched data:", response.data); // Debug API response

        setFormIData(response.data.data);
        setFormDetails(response.data);
      } catch (error) {
        console.error("Error fetching Form I data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (crewingAgentId) fetchFormIData();
  }, [crewingAgentId, selectedMonth, selectedYear]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Client-side filtering by selected Month & Year on uploadedAt date
  const filteredByDate = formIData.filter((contract) => {
    if (!contract.formI?.uploadedAt) return false;

    const uploadedDate = new Date(contract.formI.uploadedAt);
    const matchesMonth = selectedMonth ? uploadedDate.getMonth() + 1 === Number(selectedMonth) : true;
    const matchesYear = selectedYear ? uploadedDate.getFullYear() === Number(selectedYear) : true;

    return matchesMonth && matchesYear;
  });

  // Filter by search query on crew name, vessel, or indos no
  const filteredData = filteredByDate.filter((contract) => {
    const name = contract?.contractId?.crewdetails?.name?.toLowerCase() || "";
    const vessel = contract?.contractId?.vesselName?.toLowerCase() || "";
    const indos = contract?.contractId?.crewdetails?.indosno?.toLowerCase() || "";
    return name.includes(searchQuery) || vessel.includes(searchQuery) || indos.includes(searchQuery);
  });

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExportToExcel = () => {
    const exportData = filteredData.map((contract, index) => ({
      "Sr. No.": index + 1,
      "Form1 Issued Date": formatDate(contract.formI?.uploadedAt),
      Company: contract.contractId?.vesselOwnerName || "N/A",
      Vessel: contract.contractId?.vesselName || "N/A",
      "Crew Name": contract.contractId?.crewdetails?.name || "N/A",
      "Indos No.": contract.contractId?.crewdetails?.indosno || "N/A",
      Email: contract.contractId?.crewdetails?.email || "N/A",
      Rank: contract.contractId?.crewdetails?.rankname || "N/A",
      "Crewing Agent": contract.crewingAgentId?.agentName || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FormIList");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

    const monthName = selectedMonth
      ? new Date(0, selectedMonth - 1).toLocaleString("default", { month: "long" })
      : "AllMonths";
    const year = selectedYear || "AllYears";

    saveAs(dataBlob, `FormIList_${monthName}_${year}.xlsx`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  return (
    <>
      <Navbar />
      <Box p={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" fontWeight="bold">
            Form I List
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              select
              label="Month"
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Months</MenuItem>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Year"
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All Years</MenuItem>
              {Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </TextField>

            <Button variant="contained" color="primary">
              Count: {formDetails.count || 0}
            </Button>
            <Button variant="contained" sx={{ backgroundColor: "green" }} onClick={handleExportToExcel}>
              Export to Excel
            </Button>
          </Stack>
        </Stack>

       <TextField
  label="Search by Crew Name, Indos No. or Vessel"
  variant="outlined"
  size="small"
  value={searchQuery}
  onChange={handleSearch}
  sx={{ mb: 2, width: '300px' }} // or any width you prefer
/>


        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress size={40} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Typography variant="body1">No records found.</Typography>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1976D2" }}>
                    {["Sr. No.", "Vessel Info", "Crew Info", "Crewing Agent", "Form I"].map((header) => (
                      <TableCell key={header} sx={{ color: "white", fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((contract, index) => (
                    <TableRow key={contract._id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography>
                          <b>Contract Created At:</b> {formatDate(contract.createdAt)}
                        </Typography>
                        <Typography>
                          <b>form1 uploaded At</b> {formatDate(contract.formI?.uploadedAt || "N/A")}
                        </Typography>
                        <Typography>
                          <b>Company:</b> {contract.contractId?.vesselOwnerName || "N/A"}
                        </Typography>
                        <Typography>
                          <b>Vessel:</b> {contract.contractId?.vesselName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          <b>Name:</b> {contract.contractId?.crewdetails?.name}
                        </Typography>
                        <Typography>
                          <b>Indos No.:</b> {contract.contractId?.crewdetails?.indosno || "N/A"}
                        </Typography>
                        <Typography>
                          <b>Email:</b> {contract.contractId?.crewdetails?.email}
                        </Typography>
                        <Typography>
                          <b>Rank:</b> {contract.contractId?.crewdetails?.rankname || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{contract.crewingAgentId?.agentName || "N/A"}</Typography>
                      </TableCell>
                      <TableCell>
                        {contract.formI?.filePath ? (
                          <a
                            href={`${baseURL}/${contract.formI.filePath.replace(/\\/g, "/")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "red" }}
                          >
                            <PictureAsPdfIcon />
                          </a>
                        ) : (
                          <Typography color="textSecondary">No File</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 30]}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default CrewingAgentFormIList;
